/**
 * MFA Service
 * Handles all MFA-related API calls
 */

import { apiPost } from './api';
import type { TOTPSetupResponse, TOTPVerifyResponse, MFALoginResponse, BackupCodesResponse } from '../types/mfa';

/**
 * Start TOTP setup - generates secret and QR code
 */
export const enableTOTP = async (): Promise<TOTPSetupResponse> => {
  const response = await apiPost<TOTPSetupResponse>('/mfa/totp/enable');
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to enable TOTP');
  }
  return response.data;
};

/**
 * Verify TOTP setup with 6-digit code
 */
export const verifyTOTPSetup = async (code: string): Promise<TOTPVerifyResponse> => {
  const response = await apiPost<TOTPVerifyResponse>('/mfa/totp/verify-setup', { code });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to verify TOTP');
  }
  return response.data;
};

/**
 * Verify TOTP code during login
 */
export const verifyTOTPLogin = async (
  email: string,
  code: string,
  isBackupCode: boolean = false
): Promise<MFALoginResponse> => {
  const response = await apiPost<MFALoginResponse>('/mfa/totp/verify-login', {
    email,
    code,
    isBackupCode,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to verify MFA code');
  }
  return response.data;
};

/**
 * Disable TOTP for current user
 */
export const disableTOTP = async (): Promise<void> => {
  const response = await apiPost<void>('/mfa/totp/disable');
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to disable TOTP');
  }
};

/**
 * Regenerate backup codes
 */
export const regenerateBackupCodes = async (): Promise<BackupCodesResponse> => {
  const response = await apiPost<BackupCodesResponse>('/mfa/backup-codes/regenerate');
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to regenerate backup codes');
  }
  return response.data;
};
